// Directus User API utilities - Supabase認証のみに移行

import { readItems, createItem, deleteItem } from '@directus/sdk';

import { directusClient, USER_PROFILES_COLLECTION, FAVORITES_COLLECTION } from './directusApi';

// 型エラー回避のため、Directus SDKの関数を any型でラップ
const readItemsAny = readItems as any;
const createItemAny = createItem as any;
const deleteItemAny = deleteItem as any;

// 型定義
interface DirectusFavorite {
  id: string;
  supabase_user_id: string; // Supabase User ID
  target_type: string;
  target_id: string;
  date_created: string;
  date_updated: string;
}

// 定数（directusApi.tsから統一）
const TARGET_TYPE_CREATURE = 'creature';
const TARGET_TYPE_ARTICLE = 'article';
const TARGET_TYPE_DINOSAUR = 'dinosaur';

// お気に入り関連の関数
export const createFavorite = async (favoriteData: {
  supabase_user_id: string;
  target_type: 'creature' | 'article' | 'dinosaur';
  target_id: string;
}): Promise<DirectusFavorite | null> => {
  try {
    const response = await directusClient.request(
      createItemAny(FAVORITES_COLLECTION as any, favoriteData)
    );
    return {
      ...response,
      target_type: response.target_type as 'creature' | 'article' | 'dinosaur',
    } as DirectusFavorite;
  } catch (error: any) {
    console.error('Error creating favorite:', error);
    throw error;
  }
};

export const deleteFavorite = async (id: string): Promise<boolean> => {
  try {
    await directusClient.request(deleteItemAny(FAVORITES_COLLECTION as any, id));
    return true;
  } catch (error: any) {
    console.error('Error deleting favorite:', error);
    throw error;
  }
};

export const findFavoritesBySupabaseUserId = async (
  supabaseUserId: string
): Promise<DirectusFavorite[]> => {
  try {
    const response = await directusClient.request(
      readItemsAny(FAVORITES_COLLECTION as any, {
        filter: {
          supabase_user_id: {
            _eq: supabaseUserId,
          },
        },
        sort: ['-date_created'], // 降順ソート（最新のものから）
        limit: -1, // すべての結果を取得
      })
    );

    // レスポンスが配列でない場合の処理
    const favoritesArray = Array.isArray(response) ? response : [];

    return favoritesArray
      .map((favorite: any) => ({
        ...favorite,
        target_type: favorite.target_type as 'creature' | 'article' | 'dinosaur',
      }))
      .sort((a, b) => {
        // クライアント側でも確実にソートを実行（フォールバック）
        const dateA = new Date(a.date_created).getTime();
        const dateB = new Date(b.date_created).getTime();
        return dateB - dateA; // 降順ソート
      }) as DirectusFavorite[];
  } catch (error: any) {
    console.error('Error finding favorites by Supabase user ID:', error);
    throw error;
  }
};

export const findFavoriteBySupabaseUserAndTarget = async (
  supabaseUserId: string,
  targetId: string,
  targetType: 'creature' | 'article' | 'dinosaur'
): Promise<DirectusFavorite | null> => {
  try {
    const expectedTargetType =
      targetType === 'creature'
        ? TARGET_TYPE_CREATURE
        : targetType === 'dinosaur'
          ? TARGET_TYPE_DINOSAUR
          : TARGET_TYPE_ARTICLE;

    const response = await directusClient.request(
      readItemsAny(FAVORITES_COLLECTION as any, {
        filter: {
          supabase_user_id: {
            _eq: supabaseUserId,
          },
          target_id: {
            _eq: targetId,
          },
          target_type: {
            _eq: expectedTargetType,
          },
        },
      })
    );

    if (response && response.length > 0) {
      const favorite = response[0];
      return {
        ...favorite,
        target_type: favorite.target_type as 'creature' | 'article' | 'dinosaur',
      } as DirectusFavorite;
    }

    return null;
  } catch (error: any) {
    console.error('Error finding favorite by Supabase user and target:', error);
    throw error;
  }
};

export const checkIfFavorited = async (
  supabaseUserId: string,
  targetId: string,
  targetType: 'creature' | 'article' | 'dinosaur'
): Promise<DirectusFavorite | null> => {
  try {
    return await findFavoriteBySupabaseUserAndTarget(supabaseUserId, targetId, targetType);
  } catch (error: any) {
    throw error;
  }
};

// 後方互換性のためのエイリアス（削除予定）
export const findFavoritesByUserId = findFavoritesBySupabaseUserId;
export const findFavoriteByUserAndTarget = findFavoriteBySupabaseUserAndTarget;

// お気に入り関連の関数のエイリアス（AuthContext用）
export const getUserFavorites = findFavoritesBySupabaseUserId;

// AuthContext用のaddToFavorites関数（引数: supabaseUserId, targetId, targetType）
export const addToFavorites = async (
  supabaseUserId: string,
  targetId: string,
  targetType: 'creature' | 'article' | 'dinosaur'
): Promise<DirectusFavorite | null> => {
  const target_type =
    targetType === 'creature'
      ? TARGET_TYPE_CREATURE
      : targetType === 'dinosaur'
        ? TARGET_TYPE_DINOSAUR
        : TARGET_TYPE_ARTICLE;

  // 既存のお気に入りをチェック
  const existingFavorite = await findFavoriteBySupabaseUserAndTarget(
    supabaseUserId,
    targetId,
    targetType
  );

  if (existingFavorite) {
    // 既存のお気に入りがある場合は、それを返す（新規作成は不要）
    return existingFavorite;
  }

  // 新規作成
  const favoriteData = {
    supabase_user_id: supabaseUserId,
    target_id: targetId,
    target_type: target_type as 'creature' | 'article' | 'dinosaur',
  };

  return createFavorite(favoriteData);
};

// AuthContext用のremoveFromFavorites関数（引数: favoriteId, targetId, targetType）
export const removeFromFavorites = async (
  favoriteId: string,
  targetId?: string,
  targetType?: 'creature' | 'article' | 'dinosaur'
): Promise<boolean> => {
  try {
    // targetIdが利用可能な場合は、検索してからtargetIdで削除
    if (targetId && targetType) {
      const response = await directusClient.request(
        readItemsAny(FAVORITES_COLLECTION as any, {
          filter: {
            target_id: {
              _eq: targetId,
            },
            target_type: {
              _eq: targetType,
            },
          },
        })
      );

      if (response && response.length > 0) {
        // 見つかったレコードを削除
        for (const favorite of response) {
          await directusClient.request(deleteItemAny(FAVORITES_COLLECTION as any, favorite.id));
        }
        return true;
      } else {
        // 見つからない場合は既に削除済みとして成功を返す
        console.warn(
          `No favorite found with targetId: ${targetId}, considering as already removed`
        );
        return true;
      }
    } else {
      // IDで直接削除
      await directusClient.request(deleteItemAny(FAVORITES_COLLECTION as any, favoriteId));
      return true;
    }
  } catch (error: any) {
    // 404エラーの場合は既に削除済みとして成功を返す
    if (error?.message?.includes('not found') || error?.message?.includes('404')) {
      console.warn(`Favorite ${favoriteId} not found, considering as already removed`);
      return true;
    }

    console.error('removeFromFavorites エラー:', error);
    throw error;
  }
};

// ユーザープロフィール関連の関数
interface DirectusUserProfile {
  id: string;
  supabase_user_id: string;
  display_name?: string;
  avatar_url?: string;
  bio?: string;
  notification_settings?: any; // JSON型
  social_connections?: any; // JSON型
  date_created: string;
  date_updated: string;
}

export const findUserProfileBySupabaseId = async (
  supabaseUserId: string
): Promise<DirectusUserProfile | null> => {
  try {
    const response = await directusClient.request(
      readItemsAny(USER_PROFILES_COLLECTION as any, {
        filter: {
          supabase_user_id: {
            _eq: supabaseUserId,
          },
        },
      })
    );

    if (response && response.length > 0) {
      return response[0] as unknown as DirectusUserProfile;
    }

    return null;
  } catch (error: any) {
    console.error('Error finding user profile by Supabase ID:', error);
    throw error;
  }
};

export const createUserProfile = async (profileData: {
  supabase_user_id: string;
  display_name?: string;
  avatar_url?: string;
  bio?: string;
  notification_settings?: any;
  social_connections?: any;
}): Promise<DirectusUserProfile | null> => {
  try {
    const response = await directusClient.request(
      createItemAny(USER_PROFILES_COLLECTION as any, profileData)
    );
    return response as unknown as DirectusUserProfile;
  } catch (error: any) {
    console.error('Error creating user profile:', error);
    throw error;
  }
};

export const updateUserProfile = async (
  profileId: string,
  profileData: {
    display_name?: string;
    avatar_url?: string;
    bio?: string;
    notification_settings?: any;
    social_connections?: any;
  }
): Promise<DirectusUserProfile | null> => {
  try {
    const { updateItem } = await import('@directus/sdk');
    const updateItemAny = updateItem as any;
    const response = await directusClient.request(
      updateItemAny(USER_PROFILES_COLLECTION, profileId, profileData)
    );
    return response as unknown as DirectusUserProfile;
  } catch (error: any) {
    console.error('Error updating user profile:', error);
    throw error;
  }
};
